import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useOmnisearchState } from "hooks/omnisearch-v2";
import { useCallback, useMemo, useState } from "react";
import { breakApartOmnisearch } from "utils/omnisearchUtils";

import { DEFAULT_STAGES, STAGE_ENUM_VALUES, STAGE_LABELS } from "./stages";

function sameStageSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((value) => bSet.has(value));
}

/**
 * Top-bar Status filter — a checkbox multi-select over the lifecycle stages,
 * mirroring the Waste Fulfillment dashboard's Status dropdown. It reads/writes the
 * same `stage` omnisearch key the Queue stat cards and hand-typed filters use, so
 * all three stay in sync. Selection is committed when the menu closes:
 *  - the default set (everything but Resolved) clears the key, restoring the
 *    server's hide-Resolved default;
 *  - any other set is written pipe-joined — including all five stages, which
 *    explicitly lifts the hide-Resolved default;
 *  - an empty set writes `stage:none`, which matches no rows.
 */
export function StageFilterSelect() {
  const { omnisearch, editOmnisearchKeyValues, removeOmnisearchKeyValue } = useOmnisearchState();

  const [open, setOpen] = useState(false);
  const [intermediateStages, setIntermediateStages] = useState<string[]>([]);

  const committedStages = useMemo(() => {
    // Parsed with the omnisearch hook's own grammar (not an ad-hoc regex). The
    // raw value keeps any quotes the user typed, so strip them before matching.
    const raw = breakApartOmnisearch(omnisearch).kvpValues.find(([key]) => key === "stage")?.[1];
    const value = raw?.replace(/^"|"$/g, "");
    if (value === undefined || value === "") return [...DEFAULT_STAGES];
    if (value.toLowerCase() === "none") return [];
    // Uppercase before matching: the server's stage filter uppercases its input,
    // so a hand-typed `stage:resolved` filters the grid — parsing it
    // case-sensitively here would show the default set instead, and the next
    // open+close would commit that default and silently delete the typed filter.
    const parsed = [
      ...new Set(
        value
          .split(/[|,]/)
          .map((stage) => stage.trim().toUpperCase())
          .filter((stage) => STAGE_ENUM_VALUES.includes(stage))
      ),
    ];
    return parsed.length > 0 ? parsed : [...DEFAULT_STAGES];
  }, [omnisearch]);

  const selectedStages = open ? intermediateStages : committedStages;

  const handleOpen = useCallback(() => {
    setIntermediateStages([...committedStages]);
    setOpen(true);
  }, [committedStages]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (intermediateStages.length === 0) {
      editOmnisearchKeyValues([["stage", "none"]]);
    } else if (sameStageSet(intermediateStages, DEFAULT_STAGES)) {
      removeOmnisearchKeyValue("stage");
    } else {
      editOmnisearchKeyValues([["stage", intermediateStages.join("|")]]);
    }
  }, [intermediateStages, editOmnisearchKeyValues, removeOmnisearchKeyValue]);

  const handleChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const raw = typeof value === "string" ? value.split(",") : value;
    // MUI Select fires onChange for clicks on ANY child, including the
    // Show/Hide All header controls, which carry no value and land as
    // undefined — ignore those events entirely (mirrors StatusFilter).
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    setIntermediateStages(raw.filter((stage) => STAGE_ENUM_VALUES.includes(stage)));
  }, []);

  const handleToggleAll = useCallback(() => {
    setIntermediateStages((current) =>
      current.length === STAGE_ENUM_VALUES.length ? [] : [...STAGE_ENUM_VALUES]
    );
  }, []);

  const handleReset = useCallback(() => {
    setIntermediateStages([...DEFAULT_STAGES]);
  }, []);

  const isAtDefault = sameStageSet(selectedStages, DEFAULT_STAGES);

  return (
    <FormControl variant="standard" size="small" sx={{ width: 170 }}>
      <InputLabel id="gsc-stage-filter-label">Status</InputLabel>
      <Select
        labelId="gsc-stage-filter-label"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        value={selectedStages}
        onChange={handleChange}
        label="Status"
        variant="standard"
        sx={{
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
        renderValue={(selected) => {
          if (selected.length === STAGE_ENUM_VALUES.length) {
            return `All (${selected.length})`;
          }
          if (selected.length === 0) {
            return "None";
          }
          if (selected.length <= 2) {
            return selected.map((stage) => STAGE_LABELS[stage] ?? stage).join(", ");
          }
          return `${selected.length} selected`;
        }}
        MenuProps={{
          // Prevent the menu from locking body scroll, which triggers the
          // DataGrid's ResizeObserver (mirrors StatusFilter).
          disableScrollLock: true,
          MenuListProps: {
            autoFocusItem: selectedStages.length !== STAGE_ENUM_VALUES.length,
          },
          PaperProps: {
            sx: { minWidth: 300 },
          },
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedStages.length === STAGE_ENUM_VALUES.length}
                indeterminate={
                  selectedStages.length > 0 && selectedStages.length < STAGE_ENUM_VALUES.length
                }
                onChange={handleToggleAll}
              />
            }
            label="Show/Hide All"
            sx={{ margin: 0 }}
          />
          {!isAtDefault && <Button onClick={handleReset}>Reset</Button>}
        </Box>
        {Object.entries(STAGE_LABELS).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            <Checkbox checked={selectedStages.includes(value)} />
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
